import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { toast } from 'react-toastify';
import LegalModal from '../components/LegalModal';

const SimpleLogin = () => {
  const navigate = useNavigate();
  const { user, userClaims, loading } = useSimpleAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [legalModalType, setLegalModalType] = useState(null);

  // Redirección automática si ya está autenticado
  React.useEffect(() => {
    if (loading) return;
    
    if (user && userClaims) {
      console.log('🔧 SimpleLogin: Usuario ya autenticado, redirigiendo...', userClaims);
      
      if (userClaims.globalAdmin === true || userClaims.role === 'globalAdmin') {
        navigate('/global-admin', { replace: true });
      } else if (userClaims.isAdmin === true || userClaims.role === 'businessAdmin') {
        navigate('/admin', { replace: true });
      } else if (userClaims.role === 'client' && userClaims.tenantId) {
        navigate('/booking', { replace: true });
      } else {
        toast.error('❌ Error: No se encontró información del negocio asociado');
      }
    }
  }, [user, userClaims, loading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('❌ Por favor completa todos los campos');
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('🔧 SimpleLogin: Intentando iniciar sesión...');
      
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      console.log('🔧 SimpleLogin: Inicio de sesión exitoso:', userCredential.user.uid);
      
      toast.success('¡Inicio de sesión exitoso!');
      
      // La redirección se manejará automáticamente en el useEffect
      
    } catch (error) {
      console.error('🔧 SimpleLogin: Error en inicio de sesión:', error);
      
      let errorMessage = 'Error al iniciar sesión';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Usuario no encontrado';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Contraseña incorrecta';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email inválido';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos fallidos. Intenta más tarde';
          break;
        default:
          errorMessage = error.message;
      }
      
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      toast.error('Ingresa tu email arriba para enviarte el enlace');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, formData.email);
      toast.success('Te enviamos un enlace para restablecer la contraseña');
    } catch (error) {
      toast.error(error.message || 'No se pudo enviar el email de recuperación');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50" aria-hidden />
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Banner a ancho completo en todas las pantallas */}
      <div className="w-screen -mx-4 sm:w-full sm:mx-auto sm:max-w-md mb-6">
        <img src={'/icons/icon-512.png?v=4'} alt={'Reserberry'} className="w-full h-auto object-contain" />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Logo compacto ocultado porque ya mostramos banner a ancho completo */}
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reserberry
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Accede a tu cuenta de Reserberry
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="tu@email.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Tu contraseña"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Iniciando sesión...
                </div>
              ) : (
                '> Iniciar Sesión'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResetPassword}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <div className="text-center pt-4 border-t border-gray-200 mt-6">
            <p className="text-sm text-gray-600 mb-2">
              ¿Eres cliente? Pide a tu negocio su enlace de reservas
            </p>
            <p className="text-xs text-gray-500">
              Al usar la app, aceptas nuestros{' '}
              <button 
                type="button"
                onClick={() => setLegalModalType('terms')}
                className="text-primary-600 hover:text-primary-700 underline"
              >
                términos de servicio
              </button>{' '}
              y{' '}
              <button 
                type="button"
                onClick={() => setLegalModalType('privacy')}
                className="text-primary-600 hover:text-primary-700 underline"
              >
                política de privacidad
              </button>
            </p>
          </div>
        </form>

        {/* Enlace discreto para negocios */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            ¿Tienes un negocio?{' '}
            <Link 
              to="/register-business" 
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Regístrate aquí
            </Link>
          </p>
        </div>

        {/* Footer legal - usa modales para PWA */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex justify-center items-center gap-4 text-xs text-gray-400">
            <button 
              onClick={() => setLegalModalType('legal')}
              className="hover:text-gray-600 transition-colors"
            >
              Aviso legal
            </button>
            <span>·</span>
            <button 
              onClick={() => setLegalModalType('privacy')}
              className="hover:text-gray-600 transition-colors"
            >
              Privacidad
            </button>
            <span>·</span>
            <button 
              onClick={() => setLegalModalType('terms')}
              className="hover:text-gray-600 transition-colors"
            >
              Términos
            </button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">
            © {new Date().getFullYear()} Reserberry
          </p>
        </div>
      </div>

      {/* Modal legal para PWA */}
      <LegalModal 
        isOpen={legalModalType !== null} 
        onClose={() => setLegalModalType(null)} 
        type={legalModalType} 
      />
    </div>
  );
};

export default SimpleLogin;
